/**
 * Available Subscriptions Hook
 * 
 * React Query hook for fetching available subscriptions for purchase at a club.
 * Automatically uses clubId from appRuntime.
 * 
 * Returns the full React Query result with derived convenience properties.
 * This follows the "extended return" pattern (see useClubQuery for reference).
 * 
 * @module subscription/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useClubId } from '~/services/appRuntime';
import { subscriptionService } from '../api/subscriptionService';

export interface UseAvailableSubscriptionsOptions {
  /** Whether the query is enabled (default: true if clubId exists) */
  enabled?: boolean;
}

/**
 * Fetch available subscriptions (memberships and passes) for a club
 * 
 * Returns the full React Query result plus derived convenience fields.
 * 
 * @example
 * ```tsx
 * // Simple usage - derived fields only
 * const { memberships, passes, isLoading, error } = useAvailableSubscriptions();
 * 
 * // Advanced usage - access full React Query state
 * const { isFetching, dataUpdatedAt, memberships } = useAvailableSubscriptions();
 * ```
 */
export function useAvailableSubscriptions(
  options: UseAvailableSubscriptionsOptions = {}
) {
  const { enabled = true } = options;
  const [clubId] = useClubId();
  const clubIdNumber = clubId ? Number(clubId) : null;
  
  const query = useQuery({
    queryKey: clubIdNumber
      ? queryKeys.subscriptions.available(clubIdNumber)
      : queryKeys.disabled.subscriptionsAvailable(),
    queryFn: async () => {
      if (!clubIdNumber) {
        throw new Error('Club ID is required');
      }
      
      const result = await subscriptionService.getAvailableSubscriptions(clubIdNumber);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch available subscriptions');
      }
      
      return result.payload;
    },
    enabled: enabled && !!clubIdNumber,
    ...cachePresets.stable, // Subscription offerings rarely change during a session
  });

  // Derived convenience properties
  const memberships = query.data?.memberships ?? [];
  const passes = query.data?.passes ?? [];

  return {
    ...query,
    // Derived convenience fields
    memberships,
    passes,
  };
}
